import { QueryClient } from '@tanstack/react-query';
import { useUserProfile } from '../../api/profile';
import { usePleriStatus } from '../onboarding/apis/pleri-status';
import api from '../../lib/api';
import { MEMORY_QUERY_KEY } from '../../api/memory';

// Onboarding status interface
export interface OnboardingStatus {
  isOnboarded: boolean;
  isAdmin: boolean;
}

// Prefetch user profile
export async function prefetchUserProfile(queryClient: QueryClient) {
  return queryClient.prefetchQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const response = await api.get('/api/base/users/me');
      return response.data;
    },
  });
}

// Prefetch Pleri status
export async function prefetchPleriStatus(queryClient: QueryClient) {
  return queryClient.prefetchQuery({
    queryKey: ['pleriStatus'],
    queryFn: async () => {
      const { data } = await api.get<{ data: { isOnboarded: boolean } }>(
        '/api/core/pleri/status',
      );
      return {
        ...data?.data,
      };
    },
  });
}

// Prefetch all onboarding data in parallel
export async function prefetchOnboardingData(queryClient: QueryClient) {
  await Promise.all([
    prefetchPleriStatus(queryClient),
    prefetchUserProfile(queryClient),
  ]);
}

// Function to check if user has onboarded (legacy - kept for backward compatibility)
// This now uses the Pleri status API
export async function checkOnboardingStatus(): Promise<OnboardingStatus> {
  try {
    // Check Pleri status for isOnboarded flag
    let pleriStatus: { isOnboarded: boolean } | null = null;

    try {
      const response = await api.get<{ data: { isOnboarded: boolean } }>(
        '/api/core/pleri/status',
      );
      pleriStatus = response.data.data;
    } catch (error: unknown) {
      // For errors, assume not onboarded
      console.error('Failed to fetch Pleri status:', error);
      return {
        isOnboarded: false,
        isAdmin: false,
      };
    }

    // Get isOnboarded from Pleri status
    const isOnboarded = pleriStatus?.isOnboarded || false;

    // Determine if user is admin by checking permissions
    let isAdmin = false;
    try {
      const userProfileResponse = await api.get('/api/base/users/me');
      const userProfile = userProfileResponse.data;
      const permissions = userProfile?.permissions || [];
      isAdmin = permissions.some(
        (perm: string) => perm.includes('pleri:Write') || perm.includes('*'),
      );
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      // If we can't determine admin status, assume non-admin
      isAdmin = false;
    }

    return {
      isOnboarded,
      isAdmin,
    };
  } catch (error) {
    console.error('Error checking onboarding status:', error);
    // On error, assume not onboarded
    return {
      isOnboarded: false,
      isAdmin: false,
    };
  }
}

// User Memory Types
export interface UserMemory {
  nickName?: string;
  role?: string;
  communicationPreference?: string;
  currentGoalOrFocus?: string;
  otherNotes?: string[];
}

export interface UserMemoryUpdateRequest {
  memory?: UserMemory;
  answeredAllOnboardingQuestions?: boolean;
  skippedOnboardingQuestions?: boolean;
  lastAnsweredAllQuestionsAt?: string;
}

export interface UserMemoryUpdateResponse {
  data: UserMemoryUpdateRequest;
}

// Update user memory (role, communication preferences, etc.)
export async function updateUserMemory(
  data: {
    nickName?: string;
    role?: string;
    communicationPreference?: string;
    detailLevel?: 'brief' | 'standard' | 'in-depth';
    technicalLevel?: 'less-technical' | 'mixed' | 'very-technical';
  },
  queryClient: QueryClient,
): Promise<UserMemoryUpdateRequest> {
  try {
    // Map onboarding data to memory format
    const memoryData: UserMemoryUpdateRequest = {
      memory: {},
    };

    if (data.nickName) {
      memoryData.memory = {
        ...memoryData.memory,
        nickName: data.nickName,
      };
    }

    if (data.role) {
      memoryData.memory = {
        ...memoryData.memory,
        role: data.role,
      };
    }

    // Combine detail level and technical level into communication preference
    if (data.detailLevel || data.technicalLevel) {
      const parts: string[] = [];
      if (data.detailLevel) {
        parts.push(`Detail level: ${data.detailLevel}`);
      }
      if (data.technicalLevel) {
        parts.push(`Technical level: ${data.technicalLevel}`);
      }
      memoryData.memory = {
        ...memoryData.memory,
        communicationPreference: parts.join(', '),
      };
    }

    if (memoryData.memory && Object.keys(memoryData.memory).length > 0) {
      const response = await api.put<UserMemoryUpdateResponse>(
        '/api/core/pleri/memory',
        memoryData,
      );
      await queryClient.invalidateQueries({ queryKey: MEMORY_QUERY_KEY });
      return response.data.data;
    }

    // Return empty response if no data to update
    return memoryData;
  } catch (error) {
    console.error('Failed to update user memory:', error);
    throw error;
  }
}

// Mock function to mark onboarding as complete
export async function completeOnboarding(data: {
  name: string;
  role: string;
  roleOther?: string;
  toolsConnected?: string[];
  questionsSelected?: string[];
}): Promise<void> {
  // In a real implementation, this would POST to the API
  localStorage.setItem(
    'plerion_onboarding_status',
    JSON.stringify({
      isOnboarded: true,
      isAdmin: true,
    }),
  );
  localStorage.setItem('plerion_onboarding_data', JSON.stringify(data));
}

// Hook to check onboarding status using React Query
// This uses the Pleri status API
export function useOnboardingStatus() {
  const { data: pleriStatus, isLoading } = usePleriStatus();

  const status = pleriStatus?.isOnboarded ?? false;

  return { status, isLoading };
}

// Helper to check if user is admin
// Admin is determined by having `pleri:Write` or `*` permission
export function useIsAdmin(): boolean {
  const { data: userProfile } = useUserProfile();

  if (!userProfile) {
    return false;
  }

  // Check if user has admin permissions: pleri:Write or *
  const permissions = userProfile.permissions || [];
  const hasAdminPermission = permissions.some(
    (perm: string) => perm.includes('pleri:Write') || perm.includes('*'),
  );

  return hasAdminPermission;
}
